import { useMemo } from 'react';
import { addDays, format, parse } from 'date-fns';
import { TZDate } from '@date-fns/tz';

import { TourStatusForZcheduler } from '@zg-rentals/ts-z-calendar-client';
import type { TourWebApiDto } from '@zg-rentals/ts-z-calendar-client';
import { useZCalendarWebApiHandler_getToursForListing } from '@zg-rentals/ts-z-calendar-client-react-query';

import useListingTourConfig from './useListingTourConfig';
import { ZCALENDAR_TOURS_QUERY_TAG } from '../constants/queryTags';
import { convertDateToLocalDate, convertLocalDateTimeToDate, formatTourTimeRange } from '../utils/formatTime';

// Days rendered as chips and per-day event buckets.
export const UPCOMING_TOURS_WINDOW_DAYS = 3;

// Days the status tiles and the empty-state gate speak for, per design: wider than the rendered window
// so a tour beyond the visible days still shows up in the counts.
export const UPCOMING_TOURS_COUNTS_WINDOW_DAYS = 14;

const FETCHED_TOUR_STATUSES = [
  TourStatusForZcheduler.CANCELLED,
  TourStatusForZcheduler.CONFIRMED,
  TourStatusForZcheduler.REQUESTED,
];

const DATE_KEY_FORMAT = 'yyyy-MM-dd';

export type UpcomingTourEvent = {
  // The DTO types this required, but at runtime a tour can lack a messaging thread; normalized to
  // undefined so consumers branch on the type instead of remembering to treat '' as missing.
  conversationId: string | undefined;
  renterName: string;
  startMs: number;
  timeRange: string;
  tourId: string;
  tourStatus: TourStatusForZcheduler;
};

export type UpcomingToursDay = {
  date: Date;
  events: Array<UpcomingTourEvent>;
};

export type UpcomingTourCounts = {
  booked: number;
  canceled: number;
  requested: number;
};

type UseUpcomingToursDataOptions = {
  daysAhead?: number;
  enabled?: boolean;
};

// A tour's true instant: its wall-clock start interpreted in the listing's timezone. An unusable
// zoneId yields NaN, so fall back to the viewer's reading of the wall clock — a usable instant for
// sorting and deep-link picking — instead of leaking a NaN startMs to consumers.
export const getTourStartInstant = ({ startDateTime, zoneId }: TourWebApiDto) => {
  const instant = new TZDate(
    startDateTime.date.year,
    startDateTime.date.month - 1,
    startDateTime.date.day,
    startDateTime.time.hour,
    startDateTime.time.minute,
    zoneId,
  ).getTime();

  return Number.isFinite(instant) ? instant : convertLocalDateTimeToDate(startDateTime).getTime();
};

// The listing's calendar day, straight from the DTO's wall clock. Day placement, the day headers,
// and the fetch window all key on the listing's clock — the same clock the tour calendar page and
// the backend's fromDate/toDate filtering use — so every surface agrees on which day a tour falls.
export const getListingDateKey = ({ startDateTime }: TourWebApiDto) =>
  format(convertLocalDateTimeToDate(startDateTime), DATE_KEY_FORMAT);

const getWallClockMinutes = ({ startDateTime: { time } }: TourWebApiDto) => time.hour * 60 + time.minute;

const toUpcomingTourEvent = (tour: TourWebApiDto): UpcomingTourEvent => ({
  conversationId: tour.conversationId || undefined,
  renterName: tour.bookedByName,
  startMs: getTourStartInstant(tour),
  timeRange: formatTourTimeRange(tour.startDateTime, tour.endDateTime),
  tourId: tour.id,
  tourStatus: tour.tourStatus,
});

const countByStatus = (events: Array<UpcomingTourEvent>, tourStatus: TourStatusForZcheduler) =>
  events.filter((event) => event.tourStatus === tourStatus).length;

/**
 * Data source for the redesigned Upcoming Tours widget: fetches requested, booked, and canceled
 * tours over the upcoming window and derives per-status counts plus per-day event buckets.
 * Days are the listing's calendar days: the window starts at the listing's "today" and tours bucket
 * by their wall-clock date, matching the tour calendar page and the backend's listing-local reading
 * of the fromDate/toDate range. The counts span a wider window than the rendered days, so the tiles
 * surface tours the day list cannot show; `countedEvents` exposes that wider set, chronologically,
 * for consumers that need to reach a counted tour beyond the rendered days. Every tour counts as its
 * own status — a cancellation stays a cancellation even if the same renter has since rebooked.
 */
const useUpcomingToursData = (
  listingAlias: string,
  { daysAhead = UPCOMING_TOURS_WINDOW_DAYS, enabled = true }: UseUpcomingToursDataOptions = {},
) => {
  // Shares a query key with the widget's other config reads, so the zone costs no extra request.
  const { isPending: isTourConfigPending, zoneId } = useListingTourConfig(listingAlias, { enabled });

  const listingNow = zoneId ? new TZDate(Date.now(), zoneId) : undefined;
  // An absent or unusable zoneId falls back to the viewer's clock so the widget degrades instead of
  // going dark; a config that settled by erroring still lets the tours fetch proceed on the fallback.
  const now = listingNow && Number.isFinite(listingNow.getTime()) ? listingNow : new Date();
  // Keyed to the calendar day instead of frozen at mount, so any render after midnight (e.g. from a
  // refetch-on-focus state change) moves the window and the request range to the new day.
  const todayKey = format(now, DATE_KEY_FORMAT);
  const today = useMemo(() => parse(todayKey, DATE_KEY_FORMAT, new Date()), [todayKey]);

  // Widened to cover the rendered window when a caller asks for more days than the counts window: the
  // tiles must never report fewer tours than the day list already shows.
  const countsWindowDays = Math.max(daysAhead, UPCOMING_TOURS_COUNTS_WINDOW_DAYS);

  const windowDates = useMemo(
    () => Array.from({ length: daysAhead }, (_, dayOffset) => addDays(today, dayOffset)),
    [today, daysAhead],
  );

  const countsWindowKeys = useMemo(
    () =>
      Array.from({ length: countsWindowDays }, (_, dayOffset) => format(addDays(today, dayOffset), DATE_KEY_FORMAT)),
    [today, countsWindowDays],
  );

  // The backend interprets fromDate/toDate as calendar days in the listing's timezone (inclusive),
  // so a window anchored to the listing's "today" requests exactly the counts window — wider than the
  // rendered days, which the buckets below trim it back to. The fetch waits for the tour config so
  // the first request is already on the listing's clock.
  const { data, isError, isPending } = useZCalendarWebApiHandler_getToursForListing(
    {
      listingAlias,
      bookedByBrand: '',
      bookedByUserId: '',
      fromDate: convertDateToLocalDate(today),
      toDate: convertDateToLocalDate(addDays(today, countsWindowDays - 1)),
      tourStatuses: FETCHED_TOUR_STATUSES,
    },
    {
      enabled: enabled && Boolean(listingAlias) && !isTourConfigPending,
      meta: {
        tags: [ZCALENDAR_TOURS_QUERY_TAG],
        context: {
          api: 'getToursForListing',
          details: 'useUpcomingToursData - Error fetching upcoming tours',
        },
      },
    },
  );

  const { counts, countedEvents, days } = useMemo(() => {
    const countsWindowKeySet = new Set(countsWindowKeys);

    // The request already asks for exactly these listing-local days, so the trim only bites on a
    // drifted or cached response — but the tiles must never count a day nothing renders. Ordered by
    // listing day, then by wall clock within the day, so the flat list and every bucket read the
    // same way. Each tour counts as its own status: a cancellation stays a cancellation even if the
    // renter has since rebooked.
    const countedTours = (data?.tours ?? [])
      .flatMap((tour) => {
        const dateKey = getListingDateKey(tour);
        return countsWindowKeySet.has(dateKey) ? [{ dateKey, tour }] : [];
      })
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey) || getWallClockMinutes(a.tour) - getWallClockMinutes(b.tour));

    // Converted once and then bucketed by reference, so a tour counted in a tile and the same tour
    // listed under a day chip are one object rather than two copies — the tiles counting a superset of
    // the listed events is structural, not something the two code paths have to agree to keep true.
    const countedEvents = countedTours.map(({ tour }) => toUpcomingTourEvent(tour));

    const eventsByDateKey = countedTours.reduce<Record<string, Array<UpcomingTourEvent>>>(
      (acc, { dateKey }, tourIndex) => {
        (acc[dateKey] ??= []).push(countedEvents[tourIndex]);
        return acc;
      },
      {},
    );

    const days = windowDates.map((date) => ({
      date,
      events: eventsByDateKey[format(date, DATE_KEY_FORMAT)] ?? [],
    }));

    return {
      counts: {
        booked: countByStatus(countedEvents, TourStatusForZcheduler.CONFIRMED),
        canceled: countByStatus(countedEvents, TourStatusForZcheduler.CANCELLED),
        requested: countByStatus(countedEvents, TourStatusForZcheduler.REQUESTED),
      },
      countedEvents,
      days,
    };
  }, [data, countsWindowKeys, windowDates]);

  return { counts, countedEvents, days, isError, isPending };
};

export default useUpcomingToursData;
